import { apiError, stripEmpty, fieldErrorsFrom } from "../../../../../utils/formErrors";
import { useSelfEmployee } from "../../../../../services/useOrgData";
import { deferProfile } from "../../../../../services/globalData";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Stack, CircularProgress } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import TopBar from "../../../../../component/TopBar";
import BtnPrimary from "../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../component/SecondaryButton";
import FormField from "../../../../../component/Formfield";
import DateField from "../../../../../component/DateField";
import { WizardCard, FormSection, FieldGrid, FieldLabel, WizardActions } from "../../../../../component/wizard";
import { selfOnboardingUpdate } from "../../../../../services/employee";
import { useNotify } from "../../../../../component/NotificationProvider";
import { tokens } from "../../../../../theme";

import { pressable } from "../../../../../component/pressableProps";
import { RULES, STEP_FIELDS, validateFields } from "./profileRules";
const STEPS = ["Personal", "Contact", "Bank", "Statutory IDs", "Emergency", "Review"];

// stepper header
const Stepper = ({ current }) => (
  <Stack direction="row" alignItems="center" sx={{ mb: "1.5rem", flexWrap: "wrap", rowGap: "0.5rem" }}>
    {STEPS.map((label, i) => {
      const done = i < current, active = i === current;
      return (
        <React.Fragment key={label}>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
            <Box sx={{ width: "1.625rem", height: "1.625rem", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "0.8125rem", fontWeight: 600, border: `1px solid ${done || active ? tokens.accent : tokens.border}`,
              background: done ? tokens.accent : active ? `${tokens.accentTint}` : tokens.surfaceSubtle,
              color: done ? "#fff" : active ? tokens.accent : tokens.textMuted }}>
              {done ? <CheckIcon sx={{ fontSize: "0.9rem" }} /> : i + 1}
            </Box>
            <Box sx={{ fontSize: "0.8125rem", fontWeight: active ? 600 : 500, color: active ? tokens.text : tokens.textMuted, whiteSpace: "nowrap" }}>{label}</Box>
          </Stack>
          {i < STEPS.length - 1 && <Box sx={{ width: "1.5rem", height: "1px", mx: "0.5rem", background: tokens.border, flexShrink: 0 }} />}
        </React.Fragment>
      );
    })}
  </Stack>
);

const Review = ({ form }) => {
  const Row = ({ label, value }) => (
    <Stack direction="row" justifyContent="space-between" sx={{ py: "0.5rem", borderBottom: `1px solid ${tokens.border}` }}>
      <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>{label}</Box>
      <Box sx={{ fontSize: "0.8125rem", fontWeight: 600, color: tokens.text, textAlign: "right" }}>{value || "—"}</Box>
    </Stack>
  );
  const addr = [form.presentBuildingName, form.presentStreet, form.presentCity, form.presentPincode].filter(Boolean).join(", ");
  // Everything the wizard collects. Seven fields were missing — nationality,
  // blood group, account holder, bank name, UAN, the emergency contact's
  // relationship and their email — so "Review" showed less than had been
  // entered, which is the one thing a review step must not do.
  return (
    <Box>
      <Row label="Date of birth" value={form.dob && new Date(form.dob).toLocaleDateString("en-GB")} />
      <Row label="Gender" value={form.gender} />
      <Row label="Marital status" value={form.maritalStatus} />
      <Row label="Nationality" value={form.nationality} />
      <Row label="Blood group" value={form.bloodGroup} />
      <Row label="Phone" value={form.phone} />
      <Row label="Present address" value={addr} />
      <Row label="Account holder" value={form.accholderName} />
      <Row label="Bank account" value={form.accNumber} />
      <Row label="IFSC" value={form.ifscCode} />
      <Row label="Bank name" value={form.bankName} />
      <Row label="PAN" value={form.panNumber} />
      <Row label="Aadhaar" value={form.aadharNumber ? `XXXX XXXX ${form.aadharNumber}` : ""} />
      <Row label="UAN" value={form.uanNumber} />
      <Row label="Emergency contact" value={[form.emergencyContactName, form.emergencyContactRelation].filter(Boolean).join(" · ")} />
      <Row label="Emergency phone" value={form.emergencyContactNumber} />
      <Row label="Emergency email" value={form.emergencyCOntactEmail} />
    </Box>
  );
};

const CompleteProfile = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  // From the self record, not the directory: the directory has none of these
  // fields, so returning to the wizard used to show an empty form however much
  // had already been entered.
  const { data: self } = useSelfEmployee();
  const me = self || {};
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [dobOpen, setDobOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    dob: null, gender: "", maritalStatus: "", nationality: "", bloodGroup: "",
    phone: "", presentBuildingName: "", presentStreet: "", presentCity: "", presentPincode: "",
    accNumber: "", ifscCode: "", bankName: "", accholderName: "",
    panNumber: "", aadharNumber: "", uanNumber: "",
    emergencyContactName: "", emergencyContactNumber: "", emergencyContactRelation: "", emergencyCOntactEmail: "",
  });

  // prefill from whatever the admin already entered
  useEffect(() => {
    if (!me?._id) return;
    setForm((f) => {
      const next = { ...f };
      Object.keys(f).forEach((k) => { if (me[k] != null && me[k] !== "") next[k] = me[k]; });
      // stored as "XXXX XXXX 1234"; the field holds the four digits
      if (next.aadharNumber) next.aadharNumber = String(next.aadharNumber).replace(/\D/g, "").slice(-4);
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?._id]);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    // Cleared on edit rather than re-checked on every keystroke: telling
    // somebody their PAN is malformed while they are still typing it is noise.
    setErrors((e) => (e[k] ? { ...e, [k]: "" } : e));
  };

  // Checked on leaving the field, which is where the book puts it.
  const check = (k) => setErrors((e) => ({ ...e, [k]: RULES[k] ? RULES[k](form[k]) : "" }));

  // Shorthand for the props every field in this wizard repeats.
  const field = (k) => ({
    value: form[k],
    onChange: (ev) => set(k, ev.target.value),
    onBlur: () => check(k),
    validation: errors[k],
  });

  const submit = async () => {
    // Every step, not just this one: Review is reached by pressing Next, and
    // Next only checked the step it was on.
    const all = validateFields(form, STEP_FIELDS.flat());
    if (Object.keys(all).length) {
      setErrors(all);
      const firstBad = STEP_FIELDS.findIndex((fields) => fields.some((f) => all[f]));
      if (firstBad >= 0) setStep(firstBad);
      notify.error("Some details need fixing before this can be submitted");
      return;
    }
    setSaving(true);
    try {
      // stripEmpty, for the reason utils/formErrors.js documents: an untouched
      // optional was being sent as "", which is a value the server has every
      // right to reject, and the wizard would then report a failure naming a
      // field nobody had filled in.
      const res = await selfOnboardingUpdate({ ...stripEmpty(form), hasBeenOnboarded: true });
      if (res?.success === false) {
        // Put the server's objection on the field and go to its step, rather
        // than a toast on Review with no way to find what was wrong.
        const serverErrors = fieldErrorsFrom(res);
        if (Object.keys(serverErrors).length) {
          setErrors(serverErrors);
          const firstBad = STEP_FIELDS.findIndex((fields) => fields.some((f) => serverErrors[f]));
          if (firstBad >= 0) setStep(firstBad);
        }
        throw apiError(res);
      }
      // The save invalidates both the directory and the self record, so the
      // gate clears on the refetch. This used to hand-patch the directory
      // cache with the whole form — writing bank details, PAN and Aadhaar
      // into a cache the server had deliberately stopped putting them in.
      notify.success("Profile completed");
      navigate("/app");
    } catch (e) { notify.error(e?.message || "Could not save profile"); }
    finally { setSaving(false); }
  };

  /**
   * Save whatever has been entered and get out of the way.
   *
   * Deliberately does not set hasBeenOnboarded: nothing was finished, and a
   * flag that says otherwise is what this whole change is undoing. The
   * dashboard, the checklist and a weekly notification carry on asking.
   */
  const skip = async () => {
    setSaving(true);
    try {
      // Blanks dropped here too: leaving early should save what was typed,
      // not overwrite the admin's prefill with a screenful of empty strings.
      await selfOnboardingUpdate(stripEmpty(form));
      notify.success("Saved — you can finish this whenever you like");
    } catch {
      // Not worth blocking on: they asked to leave, and nothing here is lost
      // that they cannot type again.
    } finally {
      setSaving(false);
      deferProfile();
      navigate("/app");
    }
  };

  const next = () => {
    const found = validateFields(form, STEP_FIELDS[step] || []);
    if (Object.keys(found).length) {
      setErrors((e) => ({ ...e, ...found }));
      return;
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  return (
    <Box sx={{ width: "100%" }}>
      <TopBar MainHeading={"Complete your profile"} breadcrumbs={[{ label: "Onboarding" }]} />
      <Box sx={{ maxWidth: "48rem", mx: "auto" }}>
        <WizardCard>
          <Stepper current={step} />

          {step === 0 && (
            <FormSection title="Personal details" description="A few basics about you.">
              <FieldGrid>
                <Box>
                  <FieldLabel>Date of birth</FieldLabel>
                  {/* past: a date of birth is always in the past, and the
                      dialog defaults to future-only — which left every day in
                      the calendar disabled and nothing selectable.
                      disableFuture and openTo="year" follow from the same
                      fact: it cannot be ahead of today, and opening on this
                      month is decades of clicking from a birth year. */}
                  <DateField value={form.dob} isDialogOpen={dobOpen} openDialog={() => setDobOpen(true)} closeDialog={() => setDobOpen(false)}
                    onSelect={(d) => { set("dob", d); setErrors((e) => ({ ...e, dob: "" })); setDobOpen(false); }}
                    past disableFuture openTo="year" fullwidth noMargin height="2.75rem"
                    validation={errors.dob} />
                </Box>
                <Box>
                  <FieldLabel>Gender</FieldLabel>
                  <FormField type="select" fullwidth noMargin height="2.75rem" {...field("gender")}
                    options={[{ name: "Male", value: "Male" }, { name: "Female", value: "Female" }, { name: "Other", value: "Other" }]} />
                </Box>
                <Box>
                  <FieldLabel>Marital status</FieldLabel>
                  <FormField type="select" fullwidth noMargin height="2.75rem" {...field("maritalStatus")}
                    options={[{ name: "Single", value: "Single" }, { name: "Married", value: "Married" }, { name: "Other", value: "Other" }]} />
                </Box>
                <Box>
                  <FieldLabel>Nationality</FieldLabel>
                  <FormField type="text" fullwidth noMargin height="2.75rem" {...field("nationality")} />
                </Box>
                <Box>
                  <FieldLabel>Blood group</FieldLabel>
                  <FormField type="text" fullwidth noMargin height="2.75rem" {...field("bloodGroup")} />
                </Box>
              </FieldGrid>
            </FormSection>
          )}

          {step === 1 && (
            <FormSection title="Contact & address" description="How we reach you and where you're based.">
              <FieldGrid>
                <Box><FieldLabel>Phone</FieldLabel><FormField type="text" fullwidth noMargin height="2.75rem" {...field("phone")} /></Box>
                <Box><FieldLabel>Building / house</FieldLabel><FormField type="text" fullwidth noMargin height="2.75rem" {...field("presentBuildingName")} /></Box>
                <Box><FieldLabel>Street</FieldLabel><FormField type="text" fullwidth noMargin height="2.75rem" {...field("presentStreet")} /></Box>
                <Box><FieldLabel>City</FieldLabel><FormField type="text" fullwidth noMargin height="2.75rem" {...field("presentCity")} /></Box>
                <Box><FieldLabel>Pincode</FieldLabel><FormField type="text" fullwidth noMargin height="2.75rem" {...field("presentPincode")} /></Box>
              </FieldGrid>
            </FormSection>
          )}

          {step === 2 && (
            <FormSection title="Bank details" description="Where your salary is paid.">
              <FieldGrid>
                <Box><FieldLabel>Account holder name</FieldLabel><FormField type="text" fullwidth noMargin height="2.75rem" {...field("accholderName")} /></Box>
                <Box><FieldLabel>Account number</FieldLabel><FormField type="text" fullwidth noMargin height="2.75rem" {...field("accNumber")} /></Box>
                <Box><FieldLabel>IFSC</FieldLabel><FormField type="text" fullwidth noMargin height="2.75rem" {...field("ifscCode")} onChange={(e) => set("ifscCode", e.target.value.toUpperCase())} /></Box>
                <Box><FieldLabel>Bank name</FieldLabel><FormField type="text" fullwidth noMargin height="2.75rem" {...field("bankName")} /></Box>
              </FieldGrid>
            </FormSection>
          )}

          {step === 3 && (
            <FormSection title="Statutory IDs" description="For payroll and statutory compliance.">
              <FieldGrid>
                <Box><FieldLabel>PAN</FieldLabel><FormField type="text" fullwidth noMargin height="2.75rem" {...field("panNumber")} onChange={(e) => set("panNumber", e.target.value.toUpperCase())} /></Box>
                {/* Masked only: the last four digits are all PayLoom keeps. */}
                <Box><FieldLabel>Aadhaar (last 4 digits only)</FieldLabel><FormField type="text" fullwidth noMargin height="2.75rem" {...field("aadharNumber")} onChange={(e) => set("aadharNumber", e.target.value.replace(/\D/g, "").slice(0, 4))} /></Box>
                <Box><FieldLabel>UAN (if any)</FieldLabel><FormField type="text" fullwidth noMargin height="2.75rem" {...field("uanNumber")} /></Box>
              </FieldGrid>
            </FormSection>
          )}

          {step === 4 && (
            <FormSection title="Emergency contact" description="Who we should reach in an emergency.">
              <FieldGrid>
                <Box><FieldLabel>Name</FieldLabel><FormField type="text" fullwidth noMargin height="2.75rem" {...field("emergencyContactName")} /></Box>
                <Box><FieldLabel>Relationship</FieldLabel><FormField type="text" fullwidth noMargin height="2.75rem" {...field("emergencyContactRelation")} /></Box>
                <Box><FieldLabel>Phone</FieldLabel><FormField type="text" fullwidth noMargin height="2.75rem" {...field("emergencyContactNumber")} /></Box>
                <Box><FieldLabel>Email</FieldLabel><FormField type="text" fullwidth noMargin height="2.75rem" {...field("emergencyCOntactEmail")} /></Box>
              </FieldGrid>
            </FormSection>
          )}

          {step === 5 && (
            <FormSection title="Review & submit" description="Check your details, then submit to finish onboarding.">
              <Review form={form} />
            </FormSection>
          )}

          <WizardActions>
            {step > 0 && <BtnSecondary onClick={back} sx={{ borderRadius: "8px" }}>Back</BtnSecondary>}
            {/* An honest way out. Somebody may not have their PAN card or an
                open bank account on their first morning, and the alternative
                to this is clicking through six blank steps — which the wizard
                permits and which leaves a profile that claims to be finished. */}
            <Box
              {...pressable(skip)}
              sx={{
                alignSelf: "center",
                fontSize: "0.875rem",
                color: tokens.textMuted,
                cursor: "pointer",
                textDecoration: "underline",
                mr: "auto",
              }}
            >
              I'll finish this later
            </Box>
            {step < STEPS.length - 1 ? (
              <BtnPrimary onClick={next} sx={{ borderRadius: "8px" }}>Next</BtnPrimary>
            ) : (
              <BtnPrimary onClick={submit} disabled={saving} sx={{ borderRadius: "8px" }}>
                {saving ? <CircularProgress size={"1.25rem"} sx={{ color: "#fff" }} /> : "Submit & finish"}
              </BtnPrimary>
            )}
          </WizardActions>
        </WizardCard>
      </Box>
    </Box>
  );
};

export default CompleteProfile;
